import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

export const realtimeExecutionService = {
  /**
   * Fetch active rule triggers (currently executing)
   */
  async getActiveExecutions() {
    try {
      const { data, error } = await supabase
        ?.from('automation_executions')
        ?.select(`
          id,
          rule_id,
          execution_status,
          triggered_at,
          matched_clients_count,
          emails_sent_count,
          error_message,
          rule:automation_rules(
            rule_name,
            trigger_type
          )
        `)
        ?.in('execution_status', ['pending', 'processing'])
        ?.order('triggered_at', { ascending: false })
        ?.limit(20);

      if (error) {
        console.error('Error fetching active executions:', error);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch active executions:', error);
      return [];
    }
  },

  /**
   * Fetch queued emails (pending delivery)
   */
  async getQueuedEmails() {
    try {
      const { data, error } = await supabase
        ?.from('automation_execution_recipients')
        ?.select(`
          id,
          execution_id,
          recipient_email,
          recipient_name,
          email_sent,
          email_sent_at,
          error_message,
          execution:automation_executions(
            rule:automation_rules(
              rule_name
            )
          )
        `)
        ?.eq('email_sent', false)
        ?.is('error_message', null)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (error) {
        console.error('Error fetching queued emails:', error);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch queued emails:', error);
      return [];
    }
  },

  /**
   * Fetch delivery status (sent/delivered/failed/bounced)
   */
  async getDeliveryStatus(limit = 100) {
    try {
      const { data, error } = await supabase
        ?.from('client_email_logs')
        ?.select(`
          id,
          client_id,
          subject,
          recipient_email,
          status,
          sent_at,
          opened_at,
          clicked_at,
          open_count,
          click_count,
          error_message,
          client:clients(
            name
          )
        `)
        ?.order('sent_at', { ascending: false })
        ?.limit(limit);

      if (error) {
        console.error('Error fetching delivery status:', error);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch delivery status:', error);
      return [];
    }
  },

  /**
   * Fetch failure logs (errors and bounces)
   */
  async getFailureLogs(limit = 50) {
    try {
      // Get failed executions
      const { data: executionFailures, error: execError } = await supabase
        ?.from('automation_executions')
        ?.select(`
          id,
          rule_id,
          execution_status,
          triggered_at,
          error_message,
          rule:automation_rules(
            rule_name,
            trigger_type
          )
        `)
        ?.eq('execution_status', 'failed')
        ?.order('triggered_at', { ascending: false })
        ?.limit(limit);

      if (execError) {
        console.error('Error fetching execution failures:', execError);
      }

      // Get failed recipient emails
      const { data: recipientFailures, error: recipError } = await supabase
        ?.from('automation_execution_recipients')
        ?.select(`
          id,
          execution_id,
          recipient_email,
          recipient_name,
          email_sent_at,
          error_message,
          execution:automation_executions(
            triggered_at,
            rule:automation_rules(
              rule_name
            )
          )
        `)
        ?.not('error_message', 'is', null)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (recipError) {
        console.error('Error fetching recipient failures:', recipError);
      }

      // Get email log failures
      const { data: emailFailures, error: emailError } = await supabase
        ?.from('client_email_logs')
        ?.select(`
          id,
          subject,
          recipient_email,
          status,
          sent_at,
          error_message,
          client:clients(
            name
          )
        `)
        ?.in('status', ['failed', 'bounced'])
        ?.order('sent_at', { ascending: false })
        ?.limit(limit);

      if (emailError) {
        console.error('Error fetching email failures:', emailError);
      }

      // Combine and format all failures
      const allFailures = [
        ...(executionFailures || [])?.map(f => ({
          type: 'execution',
          id: f?.id,
          ruleName: f?.rule?.rule_name,
          triggerType: f?.rule?.trigger_type,
          timestamp: f?.triggered_at,
          errorMessage: f?.error_message,
          status: f?.execution_status
        })),
        ...(recipientFailures || [])?.map(f => ({
          type: 'recipient',
          id: f?.id,
          ruleName: f?.execution?.rule?.rule_name,
          recipientEmail: f?.recipient_email,
          recipientName: f?.recipient_name,
          timestamp: f?.execution?.triggered_at || f?.email_sent_at,
          errorMessage: f?.error_message
        })),
        ...(emailFailures || [])?.map(f => ({
          type: 'email',
          id: f?.id,
          subject: f?.subject,
          recipientEmail: f?.recipient_email,
          clientName: f?.client?.name,
          timestamp: f?.sent_at,
          errorMessage: f?.error_message,
          status: f?.status
        }))
      ];

      // Sort by timestamp descending
      allFailures?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp));

      return toCamelCase(allFailures?.slice(0, limit));
    } catch (error) {
      console.error('Failed to fetch failure logs:', error);
      return [];
    }
  },

  /**
   * Subscribe to real-time updates for automation_executions
   */
  subscribeToExecutions(callback) {
    const channel = supabase
      ?.channel('automation_executions_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automation_executions'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to real-time updates for email logs
   */
  subscribeToEmailLogs(callback) {
    const channel = supabase
      ?.channel('email_logs_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_email_logs'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to real-time updates for execution recipients
   */
  subscribeToRecipients(callback) {
    const channel = supabase
      ?.channel('execution_recipients_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automation_execution_recipients'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};